import React from 'react';
import {
  Box, Card, CardContent, Typography, Alert, useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Science, CheckCircle, Lightbulb
} from '@mui/icons-material';

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const patentFeatures = [
    'Flexible verse display: full verses, sentences, or phrases',
    'Set your own accuracy threshold (50-100%)',
    'Real-time speech-to-text conversion with accuracy checking',
    'Instant feedback on each word as you recite',
    'Learn gradually: build on previously mastered content',
    'Advance only when accuracy goals are met',
    'Smart pause detection: stop recording automatically',
    'Visual feedback: highlight mistakes or show correct answers',
    'Comprehensive progress tracking and analytics',
    'Gain stronger memory retention through proven techniques'
  ];

  const memoryBenefits = [
    'Strengthens faith and deepens relationship with God',
    'Provides comfort and wisdom in times of need',
    'Helps combat temptation and make godly decisions',
    'Strengthens your prayer life and worship',
    'Gives you eternal perspective and hope',
    'Equips you to minister to others effectively'
  ];

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3, minHeight: '100vh' }}>
      {/* Hero Section - Benefits of Memorizing God's Word */}
      <Card
        sx={{
          p: {xs: 2, sm: 4},
          mb: 2,
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: 'surfaceContainerLow',
          textAlign: 'center',
          border: `1px solid ${theme.palette.outline.variant}`,
          width: '100%',
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            color: 'inherit',
            fontWeight: 700,
            mb: 2,
            fontSize: {xs: '1.25rem', sm: '1.8rem', md: '2rem'}, // Responsive sizing
          }}
        >
          Transform Your Walk with God Through Scripture Memory
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: 4,
            opacity: 0.95,
            color: 'inherit',
            fontWeight: 500,
            fontSize: {xs: '0.9rem', sm: '1.1rem', md: '1.25rem'},
          }}
        >
          Hiding God's Word in your heart brings countless blessings and strengthens your faith journey
        </Typography>

        {/* Scripture Quotes */}
        <Typography
          variant="body1"
          sx={{
            fontSize: {xs: '0.875rem', sm: '1rem'},
            fontStyle: 'italic',
            opacity: 0.95,
            color: 'inherit',
            mb: 2
          }}
        >
          "I have stored up your word in my heart, that I might not sin against you." — Psalm 119:11
        </Typography>
      </Card>

      {/* Benefits of Memorization */}
      <Card
        sx={{
          mb: 2,
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.outline.light}`,
          width: '100%',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 4,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Lightbulb sx={{ mr: 2, color: theme.palette.primary.main, fontSize: 32 }} />
            Blessings of Scripture Memorization
          </Typography>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {memoryBenefits.map((benefit, index) => (
                <Box
                  key={index}
                  sx={{
                    padding: '12px',
                    borderRadius: '4px',
                    backgroundColor: theme.palette.background.surfaceVariant ?
                      `${theme.palette.background.surfaceVariant}40` :
                      `${theme.palette.background.paper}80`,
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}
                >
                  <CheckCircle
                    sx={{
                      mr: 1.5,
                      mt: 0.25,
                      color: theme.palette.success.main,
                      fontSize: 16,
                      flexShrink: 0
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.8rem',
                      lineHeight: 1.3,
                      margin: 0,
                      textAlign: 'justify'
                    }}
                  >
                    {benefit}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px 4px' }}>
              <tbody>
                {Array.from({ length: 3 }, (_, rowIndex) => (
                  <tr key={rowIndex}>
                    {memoryBenefits.slice(rowIndex * 2, rowIndex * 2 + 2).map((benefit, colIndex) => (
                      <td
                        key={colIndex}
                        style={{
                          padding: '12px',
                          verticalAlign: 'top',
                          borderRadius: '4px',
                          backgroundColor: theme.palette.background.surfaceVariant ?
                            `${theme.palette.background.surfaceVariant}40` :
                            `${theme.palette.background.paper}80`
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <CheckCircle
                            sx={{
                              mr: 1.5,
                              mt: 0.25,
                              color: theme.palette.success.main,
                              fontSize: 16,
                              flexShrink: 0
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: '0.8rem',
                              lineHeight: 1.3,
                              margin: 0,
                              textAlign: 'justify'
                            }}
                          >
                            {benefit}
                          </Typography>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Technology Overview */}
      <Card
        sx={{
          mb: 2,
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.outline.light}`,
          width: '100%',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 4,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Science sx={{ mr: 2, color: theme.palette.primary.main, fontSize: 32 }} />
            Patent-Enhanced Features
          </Typography>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {patentFeatures.map((feature, index) => (
                <Box
                  key={index}
                  sx={{
                    padding: '12px',
                    borderRadius: '4px',
                    backgroundColor: theme.palette.background.surfaceVariant ?
                      `${theme.palette.background.surfaceVariant}40` :
                      `${theme.palette.background.paper}80`,
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}
                >
                  <CheckCircle
                    sx={{
                      mr: 1.5,
                      mt: 0.25,
                      color: theme.palette.success.main,
                      fontSize: 16,
                      flexShrink: 0
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.8rem',
                      lineHeight: 1.3,
                      margin: 0,
                      textAlign: 'justify'
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px 4px' }}>
              <tbody>
                {Array.from({ length: Math.ceil(patentFeatures.length / 2) }, (_, rowIndex) => (
                  <tr key={rowIndex}>
                    {patentFeatures.slice(rowIndex * 2, rowIndex * 2 + 2).map((feature, colIndex) => (
                      <td
                        key={colIndex}
                        style={{
                          padding: '12px',
                          verticalAlign: 'top',
                          borderRadius: '4px',
                          backgroundColor: theme.palette.background.surfaceVariant ?
                            `${theme.palette.background.surfaceVariant}40` :
                            `${theme.palette.background.paper}80`,
                          width: '50%'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <CheckCircle
                            sx={{
                              mr: 1.5,
                              mt: 0.25,
                              color: theme.palette.success.main,
                              fontSize: 16,
                              flexShrink: 0
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: '0.8rem',
                              lineHeight: 1.3,
                              margin: 0,
                              textAlign: 'justify'
                            }}
                          >
                            {feature}
                          </Typography>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Patent Information Footer */}
      <Alert
        severity="info"
        sx={{
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: `${theme.palette.primary.main}08`,
          border: `1px solid ${theme.palette.primary.main}20`,
          '& .MuiAlert-icon': {
            color: theme.palette.primary.main,
          },
          '& .MuiAlert-message': {
            color: theme.palette.text.primary
          }
        }}
      >
        <Typography variant="body2" sx={{ color: 'inherit' }}>
          <strong>About US Patent 9,684,437:</strong> "MEMORIZATION SYSTEM AND METHOD" -
          <br />
          Advanced memorization technology using speech recognition, accuracy analysis, and progressive learning methods.
        </Typography>
      </Alert>
    </Box>
  );
};

export default About;
